import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Field, FormikProps, useFormikContext } from "formik";
import {
  MultipleExpenseFormData,
  OtherExpenseEntry,
  OtherExpenseFormData,
  PaymentDetail,
} from "../types";
import { Delete as DeleteIcon, KeyboardArrowUp } from "@mui/icons-material";
import { KeyboardArrowDown } from "@mui/icons-material";
import PaymentDetailsFields from "./PaymentDetailsFields";
import { otherExpenseApi } from "../apiService";
import DeletedPayments from "./DeletedPayments";

interface ExpenseFormProps {
  type: string;
  index: number;
  typeIndex: number;
  onDelete: () => void;
  formikProps: FormikProps<MultipleExpenseFormData>;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({
  type,
  index,
  typeIndex,
  onDelete,
  formikProps,
}) => {
  const { values, setFieldValue } = useFormikContext<{
    expenses: OtherExpenseEntry[];
  }>();
  const expense = values.expenses[index];
  const [deletedPayments, setDeletedPayments] = useState<PaymentDetail[]>([]);
  const [showDeletedPayments, setShowDeletedPayments] = useState(false);

  const fetchDeletedPayments = async () => {
    if (expense.id) {
      const deletedPayments =
        await otherExpenseApi.getDeletedPaymentsByExpenseId(expense.id);
      setDeletedPayments(deletedPayments);
    }
  };

  useEffect(() => {
    fetchDeletedPayments();
  }, []);

  // Format ISO date to YYYY-MM-DD
  const formatDateForInput = (isoDate: string) => {
    if (!isoDate) return "";
    return isoDate.split("T")[0];
  };

  const handleOnChange = (field: string, value: string) => {
    setFieldValue(`expenses.${index}.isEdited`, true);
    setFieldValue(`expenses.${index}.${field}`, value);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        mb: 2,
        position: "relative",
      }}
    >
      {!values?.expenses?.[index]?.isDeleted && (
        <IconButton
          onClick={() => {
            formikProps.setFieldValue(`expenses.${index}.isDeleted`, true);
          }}
          color="error"
          size="small"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "error.main",
            "& .MuiSvgIcon-root": {
              fontSize: "1rem",
            },
          }}
        >
          <DeleteIcon />
        </IconButton>
      )}
      {values?.expenses?.[index]?.isDeleted && (
        <Button
          size="small"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "error.main",
            fontSize: "0.75rem",
            fontStyle: "italic",
          }}
          onClick={() => {
            formikProps.setFieldValue(`expenses.${index}.isDeleted`, false);
          }}
        >
          Undo Delete
        </Button>
      )}

      <Typography variant="subtitle2" sx={{ mb: 2, fontSize: "0.875rem" }}>
        {typeIndex === 0 ? type : `${type} ${typeIndex + 1}`}
      </Typography>

      <Grid container spacing={2}>
        {/* Date */}
        <Grid item xs={12} sm={6}>
          <Field name={`expenses.${index}.expenseDate`}>
            {({ field, meta }: any) => (
              <TextField
                {...field}
                label="Date *"
                type="date"
                fullWidth
                size="small"
                value={formatDateForInput(field.value)}
                onChange={(e) => {
                  handleOnChange("expenseDate", e.target.value);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                error={meta.touched && !!meta.error}
                helperText={meta.touched && meta.error}
                sx={{
                  "& .MuiInputBase-root": { fontSize: "0.75rem" },
                  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                }}
              />
            )}
          </Field>
        </Grid>

        {/* Category - Non-editable, pre-filled */}
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              height: "40px",
              px: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "action.hover",
            }}
          >
            <Typography
              variant="body1"
              color="primary"
              sx={{ fontSize: "0.75rem", fontWeight: 600 }}
            >
              {expense.category}
            </Typography>
          </Box>
        </Grid>

        {/* Paid To */}
        <Grid item xs={12} sm={6}>
          <Field name={`expenses.${index}.paidTo`}>
            {({ field, meta }: any) => (
              <TextField
                {...field}
                label="Paid To *"
                fullWidth
                size="small"
                placeholder="Enter vendor/company name"
                error={meta.touched && !!meta.error}
                helperText={meta.touched && meta.error}
                sx={{
                  "& .MuiInputBase-root": { fontSize: "0.75rem" },
                  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                }}
                onChange={(e) => {
                  handleOnChange("paidTo", e.target.value);
                }}
              />
            )}
          </Field>
        </Grid>

        {/* Amount */}
        <Grid item xs={12} sm={6}>
          <Field name={`expenses.${index}.amount`}>
            {({ field, meta }: any) => (
              <TextField
                {...field}
                label="Amount *"
                type="text"
                fullWidth
                size="small"
                placeholder="0.00"
                value={
                  field.value ? Number(field.value).toLocaleString("en-IN") : ""
                }
                onChange={(e) => {
                  const value = e.target.value.replace(/,/g, "");
                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                    handleOnChange("amount", value);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <Typography
                      component="span"
                      sx={{ mr: 0.5, fontSize: "0.75rem" }}
                    >
                      ₹
                    </Typography>
                  ),
                  inputProps: {
                    inputMode: "decimal",
                  },
                }}
                error={meta.touched && !!meta.error}
                helperText={meta.touched && meta.error}
                sx={{
                  "& .MuiInputBase-root": { fontSize: "0.75rem" },
                  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                }}
              />
            )}
          </Field>
        </Grid>

        {/* Contact Number */}
        <Grid item xs={12} sm={6}>
          <Field name={`expenses.${index}.contactNumber`}>
            {({ field, meta }: any) => (
              <TextField
                {...field}
                label="Contact Number"
                fullWidth
                size="small"
                placeholder="Enter contact number"
                error={meta.touched && !!meta.error}
                helperText={meta.touched && meta.error}
                sx={{
                  "& .MuiInputBase-root": { fontSize: "0.75rem" },
                  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                }}
                onChange={(e) => {
                  handleOnChange("contactNumber", e.target.value);
                }}
                inputProps={{
                  maxLength: 10,
                }}
              />
            )}
          </Field>
        </Grid>

        {/* Location */}
        <Grid item xs={12} sm={6}>
          <Field name={`expenses.${index}.location`}>
            {({ field, meta }: any) => (
              <TextField
                {...field}
                label="Location"
                fullWidth
                size="small"
                placeholder="Enter location"
                error={meta.touched && !!meta.error}
                helperText={meta.touched && meta.error}
                sx={{
                  "& .MuiInputBase-root": { fontSize: "0.75rem" },
                  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                }}
                onChange={(e) => {
                  handleOnChange("location", e.target.value);
                }}
              />
            )}
          </Field>
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <Field name={`expenses.${index}.notes`}>
            {({ field, meta }: any) => (
              <TextField
                {...field}
                label="Notes"
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Additional notes (optional)"
                error={meta.touched && !!meta.error}
                helperText={meta.touched && meta.error}
                sx={{
                  "& .MuiInputBase-root": { fontSize: "0.75rem" },
                  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                }}
                onChange={(e) => {
                  handleOnChange("notes", e.target.value);
                }}
              />
            )}
          </Field>
        </Grid>

        <PaymentDetailsFields index={index} />

        <Divider sx={{ my: 2 }} />
        {deletedPayments?.length > 0 && (
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "bold",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "gray",
                fontSize: "0.75rem",
              }}
              onClick={() => {
                setShowDeletedPayments(!showDeletedPayments);
              }}
            >
              Deleted Payments
              {showDeletedPayments ? (
                <KeyboardArrowUp />
              ) : (
                <KeyboardArrowDown />
              )}
            </Typography>
            {showDeletedPayments && (
              <>
                <DeletedPayments deletedPayments={deletedPayments} />
              </>
            )}
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default ExpenseForm;
